import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, error, user, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && user) {
      console.log('✅ User already logged in, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await login(email, password);
      // Wait a moment for user state to be set, then navigate
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 100);
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if already authenticated
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.15),transparent_25%),radial-gradient(circle_at_80%_0%,rgba(99,102,241,0.18),transparent_22%),radial-gradient(circle_at_50%_80%,rgba(14,165,233,0.16),transparent_20%)] pointer-events-none" />

      {/* Top Bar */}
      <div className="relative z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
          <div className="flex items-center space-x-3">
            <img
              src="/icon32.png"
              alt="NexusSales logo"
              className="h-10 w-auto"
            />
        <div>
              <div className="text-2xl font-semibold">NexusSales</div>
              <p className="text-sm text-slate-300">One workspace for customers &amp; conversations</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Link
              to="/register"
              className="hidden sm:inline-flex px-4 py-2 rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 text-sm font-semibold transition"
            >
              Sign Up
            </Link>
            <a
              href="#login-form"
              className="inline-flex px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-sm font-semibold shadow-lg shadow-primary-600/30"
            >
              Log In
            </a>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 pb-16 pt-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Hero Content */}
          <div className="space-y-6">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold text-slate-100 border border-white/10">
              <CheckCircleIcon className="h-4 w-4 text-emerald-300" />
              <span>CRM, SMS, and automations in one place</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight text-white">
              A simple workspace to talk with customers
            </h1>
            <p className="text-lg text-slate-200 max-w-2xl">
              Keep conversations and contacts organized across channels so reps and businesses stay responsive and on track.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/register"
                className="px-5 py-3 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-semibold shadow-lg shadow-primary-600/40 transition"
              >
                Get Started
              </Link>
              <Link
                to="/login"
                className="px-5 py-3 rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 text-white font-semibold transition"
              >
                Log In
              </Link>
        </div>
        
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-slate-200 pt-4">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="font-semibold">Unified Inbox</div>
                <p className="mt-1 text-slate-300">Keep every customer thread together.</p>
                </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="font-semibold">Contact Management</div>
                <p className="mt-1 text-slate-300">Segment, enrich, and act in one place.</p>
                </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="font-semibold">SMS & Automations</div>
                <p className="mt-1 text-slate-300">Send, schedule, and automate outreach.</p>
              </div>
            </div>
          </div>

          {/* Auth Card */}
          <div className="w-full" id="login-form">
            <div className="rounded-2xl bg-white/95 dark:bg-slate-900 text-slate-900 dark:text-white shadow-2xl shadow-black/40 border border-white/10 backdrop-blur-xl p-8">
              <h2 className="text-2xl font-bold mb-2">Sign in to Nexus</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                Manage conversations, billing, and workflows from one place.
              </p>

              <form className="space-y-5" onSubmit={handleSubmit} autoComplete="off">
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md p-4 text-sm text-red-800 dark:text-red-200">
                    {error}
            </div>
          )}
          
                <div className="space-y-1">
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Email address
              </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="you@example.com"
                />
            </div>
            
                <div className="space-y-1">
                  <label htmlFor="password" className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Password
              </label>
                  <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 pr-10 rounded-lg border border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                        <EyeIcon className="h-5 w-5" />
                  )}
                </button>
            </div>
          </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="inline-flex items-center space-x-2">
              <input
                type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500 dark:border-slate-700"
              />
                    <span className="text-slate-700 dark:text-slate-300">Remember me</span>
              </label>
                  <Link to="/forgot-password" className="text-primary-600 hover:text-primary-500">
                    Forgot password?
                  </Link>
          </div>

            <button
              type="submit"
              disabled={isLoading}
                  className="w-full py-2.5 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-semibold shadow-lg shadow-primary-600/40 disabled:opacity-60 disabled:cursor-not-allowed"
            >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </form>

              <div className="mt-6 text-center text-sm text-slate-600 dark:text-slate-300">
                Don&apos;t have an account?{' '}
                <Link to="/register" className="text-primary-600 hover:text-primary-500 font-semibold">
                  Sign up
                </Link>
              </div>
                </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login; 